import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import Handsontable from 'handsontable';
import {environment} from 'src/environments/environment';
import {HyperFormula} from 'hyperformula';
import {HotTableRegisterer} from '@handsontable/angular';
import {CellChange, CellValue, ChangeSource} from 'handsontable/common';
import {Observable, Subject} from 'rxjs';
import {ColumnSettings} from 'handsontable/settings';
import {DetailedSettings} from 'handsontable/plugins/nestedHeaders';
import {DataChange} from "./types/data-change.interface";

@Component({
  selector: 'app-handsontable',
  templateUrl: './handsontable.component.html',
  styleUrls: ['./handsontable.component.scss']
})
export class HandsontableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() id: string;
  @Input() identifier: string;
  @Input() dataset: Array<any>;
  @Input('settings') settingsInput: Handsontable.GridSettings;
  @Input() columns: Array<ColumnSettings>;
  @Input() nestedHeaders: Array<Array<string | DetailedSettings>>;
  @Input() csvExport: boolean = true;
  @Input() resetDataset: Observable<void>;
  @Input() customCellProps: Array<Handsontable.CellMeta> = [];
  @Output() onDataChange: EventEmitter<any> = new EventEmitter();
  @Output() onCreateRow: EventEmitter<any> = new EventEmitter();
  @Output() onRemoveRow: EventEmitter<any> = new EventEmitter();
  @Output() sendDataset: EventEmitter<Array<any>> = new EventEmitter();

  settings: Handsontable.GridSettings;
  datasetCopy: Array<any> = [];
  hotRegisterer: HotTableRegisterer = new HotTableRegisterer();
  hotInstance: Handsontable;
  dataChanges: any = {}

  constructor() {
  }

  ngOnInit(): void {
    this.resetDataset.subscribe(() => {
      this.resetDatasetHistory();
    });
  }

  ngAfterViewInit(): void {
    this.hotInstance = this.hotRegisterer.getInstance(this.id);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.settingsInput && changes.settingsInput.currentValue !== undefined) {
      this.handleSettingsChange(changes.settingsInput.currentValue);
    }
    if (changes.dataset && changes.dataset.currentValue !== undefined) {
      this.handleDatasetChange(changes.dataset.currentValue);
    }
    if (changes.customCellProps && changes.customCellProps.currentValue !== undefined) {
      this.handleSettingsChange(this.settings);
    }
  }

  private handleSettingsChange(settings: Handsontable.GridSettings) {
    this.settings = {
      stretchH: 'all',
      // height: this.dataset.length > 22 ? 600 : 'auto',
      width: '100%',
      colHeaders: true,
      rowHeaders: false,
      rowHeights: 40,
      selectionMode: 'single',
      fillHandle: false,
      copyPaste: false,
      trimDropdown: false,
      licenseKey: environment.hotLicenseKey,
      allowInsertRow: true,
      contextMenu: ['row_above', 'remove_row'],
      numericFormat: {
        pattern: '0,0.00',
        culture: 'fr'
      },
      formulas: {
        engine: HyperFormula,
      },
      comments: {
        displayDelay: 500,
      },
      language: 'fr-FR',
      afterChange: (changes: CellChange[] | null, source: ChangeSource) => {
        // let dataChanges = {}
        changes?.forEach(([row, prop, oldValue, newValue]) => {
          // if (oldValue === undefined) { // first change on init
          //   return;
          // }
          if (!['edit', 'Autofill.fill', 'CopyPaste.paste', 'CopyPaste.cut'].includes(source)) { // only user edited cells
            return;
          }
          if (!this.hotInstance) { // hot table not loaded
            return;
          }
          if (newValue === undefined || newValue === '') {
            newValue = null;
          }
          const col = this.hotInstance.propToCol(prop as string | number);
          let cellMeta = this.hotInstance.getCellMeta(row, col as number);
          const originalCellRow = cellMeta.row;

          if (newValue?.toString().startsWith('=') && !cellMeta.readOnly) { // is a formula on a editable cell
            cellMeta.valid = false;
          } else if (oldValue?.toString().startsWith('=') && !newValue?.toString().startsWith('=') && !cellMeta.readOnly) {
            cellMeta.valid = true;
          }

          if (cellMeta.className?.includes('required-cell') && newValue !== null) {
            cellMeta.className = cellMeta.className?.toString().replace(' required-cell', '');
            this.customCellProps = this.customCellProps.filter(csp => csp.row !== row && csp.prop !== prop);
          }

          let valid: boolean = cellMeta.valid === true
          var idEntity = this.dataset[originalCellRow].id
          if (typeof this.dataChanges[idEntity] === 'undefined') {
            this.dataChanges[idEntity] = []
          }
          if (newValue?.toString() !== this.datasetCopy[originalCellRow][prop as string]?.toString()) {
            if (!cellMeta.className?.includes('edited-cell')) {
              cellMeta.className = `${cellMeta.className} edited-cell`;
            }
            this.dataChanges[idEntity].push({
              id: this.id,
              row: this.dataset[originalCellRow].id,
              column: prop,
              value: newValue,
              valid: valid,
            })
          } else if (newValue?.toString() === this.datasetCopy[originalCellRow][prop as string]?.toString() && cellMeta.className?.includes('edited-cell')) {
            cellMeta.className = cellMeta.className.toString().replace(' edited-cell', '');
            this.dataChanges[idEntity].push(
              {
                id: this.id,
                row: this.dataset[originalCellRow].id,
                column: prop,
                value: newValue,
                remove: true,
                valid: valid,
              })
          }
        });

        this.onDataChange.emit(this.dataChanges);

        this.hotInstance?.render();
      },
      afterCreateRow: (index: number, amount: number, source?: ChangeSource) => {
        this.handleDatasetChange(this.dataset)

        this.onCreateRow.emit({row: index, amount: amount});
      },
      beforeRemoveRow: (_index: number, amount: number, physicalRows: number[], source?: ChangeSource) => {
        const ids = physicalRows.map(rowIndex => this.dataset[rowIndex].id);

        this.onRemoveRow.emit({ ids });
      },
      beforeLoadData: (sourceData: CellValue[], initialLoad: boolean, source: string | undefined) => {
        sourceData.forEach(objectEntry => {
          Object.keys(objectEntry).forEach(key => {
            if (objectEntry[key] === null) {
              delete objectEntry[key];
            }
          });
        })

        return sourceData
      },
      cells: (row, col, prop) => {
        const cellProperties: Handsontable.CellMeta = {};

        if (this.dataset[row]?.readOnly && !this.columns[col]?.readOnly) {
          cellProperties.readOnly = true;
        }

        const customCellProp = this.customCellProps?.find(ccp => ccp.row === row && ccp.prop === prop);
        if (customCellProp) {
          if (!cellProperties.className?.includes('required-cell')) {
            cellProperties.className = `${cellProperties.className} required-cell`;
          }
        } else if (cellProperties.className?.includes('required-cell')) {
          cellProperties.className = cellProperties.className.toString().replace(' required-cell', '');
        }

        if (this.dataset[row]?.isTotal && !cellProperties.className?.includes('total')) {
          cellProperties.className = `${cellProperties.className} total`;
        }

        if (this.dataset[row]?.history?.hasOwnProperty(prop)) {
          cellProperties.comment = {value: this.dataset[row]?.history[prop], readOnly: true, style: {width: 200, height: 50}};
        }

        if (this.dataset[row]?.details && col === 0) {
          cellProperties.comment = {value: this.dataset[row]?.details, readOnly: true, style: {width: 200, height: 50}};
        }

        return cellProperties;
      },
      ...settings
    };
    // this.hotInstance?.updateSettings(this.settings);
    this.hotInstance?.render();
  }

  search(event) {
    const filtersPlugin = this.hotInstance.getPlugin('filters');
    const colIndex = this.hotInstance.propToCol('searchContent');

    if (!filtersPlugin.isEnabled()) {
      filtersPlugin.enablePlugin();
    }
    if (filtersPlugin.conditionCollection) {
      filtersPlugin.clearConditions();
    }
    filtersPlugin.addCondition(colIndex as number, 'contains', [event.target.value]);
    filtersPlugin.filter();
  }

  private handleDatasetChange(dataset: Array<any>) {
    this.copyDataset(dataset);
    this.hotInstance?.render();
  }

  private copyDataset(dataset: Array<any>) {
    this.datasetCopy = JSON.parse(JSON.stringify(dataset));
  }

  resetDatasetHistory() {
    this.copyDataset(this.dataset);
    this.sendDataset.emit(this.dataset);

    if (!this.hotInstance.isDestroyed) {
      this.hotInstance.getCellsMeta().forEach(cellMeta => {
        if (cellMeta.className?.toString().includes('edited-cell')) {
          cellMeta.className = cellMeta.className.toString().replace(' edited-cell', '');
          this.hotInstance.setCellMetaObject(cellMeta.row, cellMeta.col, cellMeta);
        }
      });
      this.hotInstance.render();
    }
  }

}
